/**
 * Hedita Theme Switcher
 * Componente per cambiare palette e modalità
 */

import { Moon, Sun, Monitor } from "lucide-react";
import { useTheme, type HeditaTheme, type ThemeMode } from "@/lib/ThemeContext";

export function ThemeSwitcher() {
  const { theme, mode, isDark, setTheme, setMode, toggleMode, themes } = useTheme();

  return (
    <div className="space-y-4">
      {/* Theme Palette Selection */}
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-2 block">
          Tema Hedita
        </label>
        <div className="flex gap-2">
          {themes.map((t) => (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              className={`flex-1 flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${
                theme === t.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-muted-foreground/50"
              }`}
            >
              <div
                className="w-6 h-6 rounded-full ring-2 ring-offset-2 ring-offset-background"
                style={{
                  backgroundColor: t.color,
                  boxShadow: theme === t.id ? `0 0 0 2px ${t.color}` : 'none',
                }}
              />
              <span className="text-xs font-medium">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Mode Selection */}
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-2 block">
          Modalità
        </label>
        <div className="flex gap-2">
          <ModeButton
            mode="light"
            current={mode}
            icon={<Sun className="h-4 w-4" />}
            label="Chiaro"
            onClick={() => setMode("light")}
          />
          <ModeButton
            mode="dark"
            current={mode}
            icon={<Moon className="h-4 w-4" />}
            label="Scuro"
            onClick={() => setMode("dark")}
          />
          <ModeButton
            mode="system"
            current={mode}
            icon={<Monitor className="h-4 w-4" />}
            label="Sistema"
            onClick={() => setMode("system")}
          />
        </div>
      </div>

      {/* Quick Toggle */}
      <button
        onClick={toggleMode}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border hover:bg-muted/50 transition-colors"
      >
        {isDark ? (
          <>
            <Sun className="h-4 w-4" />
            <span className="text-sm">Passa a chiaro</span>
          </>
        ) : (
          <>
            <Moon className="h-4 w-4" />
            <span className="text-sm">Passa a scuro</span>
          </>
        )}
      </button>
    </div>
  );
}

function ModeButton({
  mode,
  current,
  icon,
  label,
  onClick,
}: {
  mode: ThemeMode;
  current: ThemeMode;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  const active = mode === current;

  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm transition-all ${
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-border text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// Compact version for header
export function ThemeToggle() {
  const { isDark, toggleMode } = useTheme();

  return (
    <button
      onClick={toggleMode}
      className="p-2 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
      title={isDark ? "Passa a chiaro" : "Passa a scuro"}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}
